import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import {
  Globe,
  Globe2,
  ArrowRight,
  Building2,
  FileCheck,
  Handshake,
  CheckCircle,
} from "lucide-react";

const InternationalSourcing = () => {
  const { t, lPath, isRTL } = useLanguage();

  const internationalCards = [
    {
      country: "Australia",
      code: "AU",
      title: "Australian Wool Sourcing & Exporter Relationships",
      tagline: "Commercial Fleece, Crossbred & Broad Micron Lots",
      desc: "We are actively engaging with Australian wool exporters, brokers, and merchants to build structured B2B supply channels for commercial raw fleece and coarse clips tailored for Indian spinning mills.",
      status: "Developing International Relationship",
      highlights: ["AWEX Testing Standards", "Crossbred & Coarse Fleece", "Direct Exporter Dialogue"],
    },
    {
      country: "New Zealand",
      code: "NZ",
      title: "New Zealand Wool Sourcing & Supplier Links",
      tagline: "Clean, High-Bulk Coarse & Carpet Grades",
      desc: "Establishing direct links with New Zealand wool merchants and trading houses to source clean, high-yield crossbred and carpet-grade wools renowned for superior color and resilience.",
      status: "Developing International Relationship",
      highlights: ["High Clean Yield %", "Carpet Speciality Grades", "Bulk Container Lots"],
    },
    {
      country: "China",
      code: "CN",
      title: "Chinese Wool Sourcing & B2B Trading Partnerships",
      tagline: "Raw Fleece, Coarse Grades & Processed Lots",
      desc: "Building relationships with leading Chinese wool handling enterprises and trading corporations to supply varied raw fibre types matching industrial carpet and textile requirements.",
      status: "Developing International Relationship",
      highlights: ["Diverse Fibre Grades", "B2B Trading Houses", "Industrial Supply Scale"],
    },
    {
      country: "Syria",
      code: "SY",
      title: "Syrian Wool Sourcing & Regional Relationships",
      tagline: "Resilient Carpet Grade Middle Eastern Wool Varieties",
      desc: "Cultivating regional supplier relationships for traditional high-crimp, resilient Middle Eastern wool varieties prized by specialized carpet yarn spinners for authentic durability.",
      status: "Developing Regional Relationship",
      highlights: ["Authentic Carpet Fibre", "High Crimp & Durability", "Traditional Sourcing"],
    },
    {
      country: "Other International Markets",
      code: "GLOBAL",
      title: "Additional Global Sourcing Origins",
      tagline: "Custom Specifications & Global Supply Partnerships",
      desc: "Evaluating additional international wool-producing regions and export houses based on specific customer micron requirements, yield considerations, and commercial suitability.",
      status: "Open to Supply Partnerships",
      highlights: ["Custom Buyer Specs", "Global Exporter Network", "Long-Term Contracts"],
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "International Sourcing",
        "item": "https://skwoolens.trivanahospitality.com/en/international-sourcing"
      }
    ]
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="International Raw Wool Sourcing & Global Partnerships | S.K. Woollens"
        description="Developing B2B raw wool sourcing and exporter relationships across Australia, New Zealand, China, and Syria for Indian spinning mills and global buyers."
        keywords="international wool sourcing, Australian wool exporters, New Zealand wool exporters, Syrian carpet wool, Chinese wool supply, raw wool import India"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/international-sourcing"
        schema={[breadcrumbSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Globe className="w-3.5 h-3.5 text-wool-bronze" />
              <span>International Trade Expansion</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("international.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("international.subtitle")}
            </p>

            <div className="p-4 bg-wool-sand/60 border-s-4 border-wool-bronze rounded-e-xl text-xs sm:text-sm font-semibold text-wool-charcoal leading-relaxed">
              {t("international.statement")}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Global Sourcing Strategy Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Handshake className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Long-Term Supplier Partnerships
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              We seek transparent, multi-season relationships with global wool exporters, brokers, and trading firms rather than opportunistic one-off trades.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <FileCheck className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Specification & Testing Clarity
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Clear parameters around micron (especially 30+ & 32-34+ carpet grades), clean yield percentages, VM limits, and lot test certificates.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Building2 className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Serving India's Spinning Hub
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Bridging international exporters with consistent commercial demand from reputable yarn and carpet manufacturing mills in Northern India.
            </p>
          </div>
        </div>
      </section>

      {/* 2.5 Global Sourcing Map Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-premium relative overflow-hidden">
          {/* Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none" />
          
          {/* Section title */}
          <div className="text-center max-w-2xl mx-auto space-y-2 mb-8 relative z-10">
            <div className="wool-badge bg-white/10 text-wool-sand border-white/20">
              <Globe className="w-3.5 h-3.5 text-wool-sand" />
              <span>Sourcing Map</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-white">
              Global Wool Sources to Bikaner
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/70">
              Visualizing our supplier connections and target sourcing routes converging on Bikaner, India.
            </p>
          </div>

          {/* Interactive SVG Map Container */}
          <div className="w-full aspect-[900/480] bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden flex items-center justify-center p-2">
            <svg
              className="w-full h-full relative z-10"
              viewBox="0 0 900 480"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Sourcing Paths with flowing animation */}
              {/* Australia Route */}
              <path
                d="M 800 380 Q 720 280 600 240"
                stroke="rgba(216, 204, 192, 0.45)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M 800 380 Q 720 280 600 240"
                stroke="#8C5D3A"
                strokeWidth="2"
                strokeLinecap="round"
                className="animate-map-path"
              />

              {/* New Zealand Route */}
              <path
                d="M 860 410 Q 760 290 600 240"
                stroke="rgba(216, 204, 192, 0.45)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M 860 410 Q 760 290 600 240"
                stroke="#8C5D3A"
                strokeWidth="2"
                strokeLinecap="round"
                className="animate-map-path"
              />

              {/* China Route */}
              <path
                d="M 740 200 Q 670 210 600 240"
                stroke="rgba(216, 204, 192, 0.45)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M 740 200 Q 670 210 600 240"
                stroke="#8C5D3A"
                strokeWidth="2"
                strokeLinecap="round"
                className="animate-map-path"
              />

              {/* Syria Route */}
              <path
                d="M 480 210 Q 540 220 600 240"
                stroke="rgba(216, 204, 192, 0.45)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M 480 210 Q 540 220 600 240"
                stroke="#8C5D3A"
                strokeWidth="2"
                strokeLinecap="round"
                className="animate-map-path"
              />

              {/* Pins & Labels */}
              {/* Bikaner, India (Destination) */}
              <g className="cursor-pointer">
                <circle cx="600" cy="240" r="10" className="fill-wool-bronze/25 animate-ping" />
                <circle cx="600" cy="240" r="5" className="fill-wool-bronze stroke-white" strokeWidth="1.5" />
                <text x="600" y="222" textAnchor="middle" className="fill-white text-[10px] font-bold tracking-wider uppercase">Bikaner (HQ)</text>
              </g>

              {/* Australia */}
              <g className="cursor-pointer">
                <circle cx="800" cy="380" r="4" className="fill-white stroke-wool-bronze" strokeWidth="1.5" />
                <text x="800" y="398" textAnchor="middle" className="fill-wool-sand text-[9px] font-semibold">Australia</text>
              </g>

              {/* New Zealand */}
              <g className="cursor-pointer">
                <circle cx="860" cy="410" r="4" className="fill-white stroke-wool-bronze" strokeWidth="1.5" />
                <text x="860" y="428" textAnchor="middle" className="fill-wool-sand text-[9px] font-semibold">New Zealand</text>
              </g>

              {/* China */}
              <g className="cursor-pointer">
                <circle cx="740" cy="200" r="4" className="fill-white stroke-wool-bronze" strokeWidth="1.5" />
                <text x="740" y="188" textAnchor="middle" className="fill-wool-sand text-[9px] font-semibold">China</text>
              </g>

              {/* Syria */}
              <g className="cursor-pointer">
                <circle cx="480" cy="210" r="4" className="fill-white stroke-wool-bronze" strokeWidth="1.5" />
                <text x="480" y="198" textAnchor="middle" className="fill-wool-sand text-[9px] font-semibold">Syria</text>
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* 3. Detailed International Country Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <div className="wool-badge">
            <Globe2 className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Target Sourcing Geographies</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            Strategic International Markets
          </h2>
          <p className="text-xs sm:text-sm text-wool-muted">
            Building reliable procurement channels across key wool-producing nations.
          </p>
        </div>

        <div className="space-y-6">
          {internationalCards.map((card, idx) => (
            <div
              key={idx}
              className="bg-white border border-wool-border rounded-2xl p-6 sm:p-8 shadow-sm hover:border-wool-bronze/50 transition space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-wool-border/60 pb-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-extrabold px-3 py-1 bg-wool-sand text-wool-bronze-dark rounded-md">
                    {card.code}
                  </span>
                  <h3 className="text-xl font-bold text-wool-charcoal">
                    {card.country}
                  </h3>
                </div>
                <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full inline-block self-start sm:self-auto">
                  {card.status}
                </div>
              </div>

              <div className="text-xs font-semibold text-wool-bronze uppercase tracking-wider">
                {card.tagline}
              </div>

              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                {card.desc}
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-2">
                {card.highlights.map((h, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-[11px] font-medium bg-wool-bg border border-wool-border/80 px-2.5 py-1 rounded-md text-wool-charcoal"
                  >
                    <CheckCircle className="w-3 h-3 text-wool-bronze" />
                    <span>{h}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. CTA for International Exporters / Suppliers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-bold font-display">
              Are You an International Wool Exporter or Broker?
            </h3>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              We invite wool exporters, merchants, and brokers from Australia, New Zealand, China, Syria, and global markets to connect with our procurement desk.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to={lPath("/for-international-suppliers")}
              className="btn-primary bg-wool-sand text-wool-charcoal hover:bg-white text-xs px-6 py-3"
            >
              <span>{t("international.partnerCta")}</span>
              <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
            </Link>
            <Link
              to={lPath("/contact")}
              className="btn-secondary bg-transparent text-white border-white/30 hover:bg-white/10 text-xs px-6 py-3"
            >
              Contact Our Sourcing Desk
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternationalSourcing;
